import { DOCUMENT } from '@angular/common';
import { Component, EventEmitter, Inject, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { DialogConfirm } from 'app/dialogs/confirm/dialog-confirm';
import { DialogSettingsConfigsAdd } from 'app/dialogs/settings/configs/add/dialog-settings-configs-add';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { ListConfigsRes } from 'app/interfaces/responses/List/listConfigsRes';
import { ConfigsService } from 'app/services/api/configs/configs.service';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { USER_ROLES } from 'common/constants/user-role.constants';
import { differenceWith } from 'lodash-es';

@Component({
    selector: 'app-settings-configs-configs',
    templateUrl: './settings-configs-configs.component.html',
})
export class SettingsConfigsConfigsComponent implements OnInit {
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild('tablePaginator') displayedDataPaginator: MatPaginator;
    @Output() onHasPendingChanges = new EventEmitter<boolean>();
    response: ListConfigsRes[];
    allResponses: ListConfigsRes[];
    form: FormGroup;
    formArray: FormArray;
    dataSource: MatTableDataSource<any>;
    tableColumns: string[] = ['context', 'key', 'value', 'action'];
    pendingChanges: ListConfigsRes[] = [];
    beforeLeaveMessage = this.translationService.translate('info_leave_form_message');
    configsPageSize: number = 10;
    loaded: boolean = false;
    userDetails: ExposedReadUserDetailsRes = null;
    userRoles = USER_ROLES;
    contextList: Array<DropDownItem> = [];

    constructor(
        @Inject(DOCUMENT) private _document: any,
        public configsService: ConfigsService,
        public lsrauthService: LsrauthService,
        private formBuilder: UntypedFormBuilder,
        public dialog: MatDialog,
        private translationService: TranslationService,
        private _fuseConfirmationService: FuseConfirmationService
    ) {
        this.lsrauthService.userDetails.subscribe((userDetails) => (this.userDetails = userDetails));

        this.form = this.formBuilder.group({
            rows: this.formBuilder.array([]),
        });

        this.formArray = this.form.get('rows') as FormArray;
    }

    ngOnInit(): void {
        this.refresh();
        this.getContexts();
    }

    refresh() {
        this.configsService.getConfigs().then((res) => {
            this.getContextTranslation(res);
            this.response = res;
            this.allResponses = res;
            this.initForm();
        });
    }

    initForm(): void {
        this.form.setControl('rows', this.formBuilder.array([]));
        this.formArray = this.form.get('rows') as FormArray;
        this.response.forEach((item) => {
            this.formArray.push(
                this.formBuilder.group({
                    id: [item.id],
                    key: [item.key],
                    value: [item.value, [Validators.required]],
                    context: [item.contextName],
                })
            );
        });
        this.dataSource = new MatTableDataSource(this.formArray.controls);
        this.loaded = true;
        this.dataSource.sort = this.sort;
        this.sort.sortChange.subscribe((sort) => {
            this.applySort(sort);
        });
        this.dataSource.paginator = this.displayedDataPaginator;
        this.form.valueChanges.subscribe((val) => {
            this.pendingChanges = differenceWith(
                val.rows,
                this.response,
                (a: ListConfigsRes, b: ListConfigsRes) => a.value == b.value && a.id == b.id
            );
            this.onHasPendingChanges.emit(this.pendingChanges.length > 0);
        });
    }

    checkHasChanges(id: number): boolean {
        return this.pendingChanges.find((item) => item.id == id) != null;
    }

    getInitialValue(id: number): string {
        let value = this.response.find((item) => item.id == id).value;
        if (value) {
            return this.translationService.translate('info_original_value', {
                value,
            });
        }
        return null;
    }

    // Resets all values to their original state from the api response
    resetValues(): void {
        this.pendingChanges = [];
        this.formArray.controls.forEach((control) => {
            let resetValue = this.response.find((item) => item.id == control.get('id').value);
            control.setValue({
                id: resetValue.id,
                key: resetValue.key,
                value: resetValue.value,
                context: resetValue.contextName,
            });
        });
    }

    searchFilter(filterValue) {
        this.response = this.allResponses;
        if (filterValue?.length == 0) {
            this.initForm();
        } else if (filterValue) {
            filterValue = filterValue.trim(); // Remove whitespace
            filterValue = filterValue.toLowerCase();
            let filterKeyValue = this.response.filter((x) => x.key.toLowerCase().includes(filterValue));
            let filterValueValue = this.response.filter((x) => x.value.toLowerCase().includes(filterValue));
            let filterContextValue = this.response.filter((x) => x.contextName.toLowerCase().includes(filterValue));
            let searchCombined = filterKeyValue
                .concat(filterValueValue)
                .concat(filterContextValue)
                .filter(function (elem, index, self) {
                    return index == self.indexOf(elem);
                });
            this.response = searchCombined;
            this.initForm();
        }
    }

    save() {
        const dialogRef = this.dialog.open(DialogConfirm, {
            autoFocus: false,
            restoreFocus: false,
            width: '450px',
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe((shouldSave) => {
            if (shouldSave) {
                this.configsService.updateConfigs(this.pendingChanges).subscribe({
                    next: () => this.refresh(),
                });
            }
        });
    }

    deleteConfigs(deletedId) {
        let key = this.response.find((item) => item.id == deletedId).key;
        let confirmationForm = this.formBuilder.group({
            title: 'Eyða kerfisstillingum',
            message: this.translationService.translate('error_confirm_delete_configs_message', { key }),
            icon: {
                show: true,
                name: 'heroicons_solid:trash',
                color: 'primary',
            },
            actions: {
                confirm: {
                    show: true,
                    label: this.translationService.translate('label_confirm'),
                    color: 'primary',
                },
                cancel: {
                    show: true,
                    label: this.translationService.translate('label_cancel'),
                },
            },
            dismissible: true,
        });

        // Open the dialog and save the reference of it
        const dialogRef = this._fuseConfirmationService.open(confirmationForm.value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result == 'confirmed') {
                this.configsService
                    .deleteConfigs({
                        id: deletedId,
                    })
                    .subscribe((result) => {
                        this.refresh();
                    });
            }
        });
    }

    // Custom sort function because of some weird behaviour due to having form controls as the table data source
    applySort(sort: Sort) {
        let data = this.formArray.controls;

        let sortedData = data.sort((a, b) => {
            const valueA = a.get(sort.active).value;
            const valueB = b.get(sort.active).value;

            if (!sort.active || sort.direction === '') {
                const valueAIndex = this.response.findIndex((item) => item.key == a.get('key').value);
                const valueBIndex = this.response.findIndex((item) => item.key == b.get('key').value);
                return valueAIndex < valueBIndex ? -1 : 1;
            }

            if (valueA < valueB) {
                return sort.direction === 'asc' ? -1 : 1;
            } else if (valueA > valueB) {
                return sort.direction === 'asc' ? 1 : -1;
            } else {
                return 0;
            }
        });
        this.dataSource = new MatTableDataSource(sortedData);
        this.dataSource.paginator = this.displayedDataPaginator;
    }

    getContexts() {
        this.configsService.listConfigContexts().subscribe((res) => {
            this.contextList = res.map((context) => {
                return {
                    value: context.id,
                    viewValue: context.contextName,
                } as DropDownItem;
            });
        });
    }

    getContextTranslation(res) {
        res.forEach((r) => {
            r.contextName = this.translationService.translate('label_context_' + r.contextName);
        });
        return res;
    }

    addSettings() {
        const dialogRef = this.dialog.open(DialogSettingsConfigsAdd, {
            autoFocus: false,
            restoreFocus: false,
            width: '450px',
            disableClose: true,
            data: {
                data: this.contextList,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            this.refresh();
        });
    }

    searchContext(context) {
        this.response = this.allResponses;
        if (context != null) {
            this.response = this.response.filter((x) => x.contextName.toLowerCase() == context.toLowerCase());
        }
        this.initForm();
    }
}
